<template lang="pug">
.selectTabs__container.position-relative(
  ref="dropdownRef"
  :class="{ 'selectTabs--open': open }"
)
  .selectTabs__toggle.d-flex.align-items-center.px-4.gap-2.fw-bold(
    role="combobox"
    aria-haspopup="listbox"
    :aria-expanded="open.toString()"
    aria-controls="selectTabsDropdown"
    :aria-activedescendant="`option-${selected}`"
    tabindex="0"
    @click="emit('update:open', !open)"
    @keydown.down.prevent="focusNext()"
    @keydown.up.prevent="focusPrev()"
    @keydown.enter.prevent="emitSelection(focusedTab)"
    @keydown.esc.prevent="emit('update:open', false)"
  )
    span.selectTabs__selected {{ selected }}
    span.selectTabs__icon
      EcreativAtomsSvgIcon(iconName="chevron-down", iconSize="sm")

  ul.selectTabs__dropdown.position-absolute.top-100.start-0.end-0.py-2.m-0.bg-white.list-unstyled(
    v-if="open"
    id="selectTabsDropdown"
    role="listbox"
  )
    li.selectTabs__option.py-2.px-4.fw-bold(
      v-for="tab in availableTabs"
      :id="`option-${tab}`"
      :key="tab"
      :class="{ 'selectTabs__option--active': focusedTab === tab }"
      role="option"
      :aria-selected="tab === selected"
      @click.stop="emitSelection(tab)"
    ) {{ tab }}
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps<{
  tabs: string[];
  selected: string;
  open: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:selected', tab: string): void;
  (e: 'update:open', val: boolean): void;
}>();

const dropdownRef = ref<HTMLElement>();
const focusedTab = ref(props.selected);

const normalizedSelected = computed(() =>
  typeof props.selected === 'string' ? props.selected.trim().toLowerCase() : '',
);

const availableTabs = computed(() =>
  props.tabs.filter(
    (tab) =>
      typeof tab === 'string' &&
      tab.trim().toLowerCase() !== normalizedSelected.value,
  ),
);

watch(
  () => props.selected,
  (val) => {
    focusedTab.value = val;
  },
);

function emitSelection(tab: string) {
  emit('update:selected', tab);
  emit('update:open', false);
}

function focusNext() {
  const tabs = availableTabs.value;
  const currentIndex = tabs.indexOf(focusedTab.value);
  const nextIndex = (currentIndex + 1) % tabs.length;
  focusedTab.value = tabs[nextIndex];
}

function focusPrev() {
  const tabs = availableTabs.value;
  const currentIndex = tabs.indexOf(focusedTab.value);
  const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  focusedTab.value = tabs[prevIndex];
}

function onClickOutside(event: MouseEvent) {
  if (props.open && !dropdownRef.value?.contains(event.target as Node)) {
    emit('update:open', false);
  }
}

onMounted(() => {
  document.addEventListener('click', onClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside);
});
</script>

<style scoped lang="scss">
$block: '.selectTabs';

#{$block} {
  $block: &;

  #{$block}__toggle {
    min-height: 55px;
    cursor: pointer;
    border-left: 1px solid #ccc;
    border-top-right-radius: 55px;
    border-bottom-right-radius: 55px;

    #{$block}__icon {
      transition: transform 0.2s ease;
    }
  }

  &#{$block}--open {
    #{$block}__icon {
      transform: rotate(180deg);
    }
  }

  #{$block}__dropdown {
    border-radius: 12px;
    z-index: 9999;
    box-shadow: 0 4px 8px rgba(#000, 0.1);
  }

  #{$block}__option {
    cursor: pointer;
    transition: background 0.2s;

    &:hover,
    &--active {
      background: #f0f0f0;
    }
  }
}
</style>
